import {LngLatBounds} from 'maplibre-gl';
import {getBrightness as computeBrightness, getDeckLayer, getLights, getTransitionDuration, setLights as setMapLights} from './maplibre-compat';
import {parseCSSColor} from 'csscolorparser';
import {clamp, includes, lerp, luminance, valueOrDefault} from './helpers';
import * as SunCalc from 'suncalc';

const HOUR = 3600000;
const BG_LAYER_IDS = ['background', 'background-underground'];

// The map center must move at least this many degrees in latitude or longitude
// before the sun-position-dependent lighting is refreshed, so it tracks large
// pans without updating on every move.
const DAY = 86400000;

const SUNLIGHT_REFRESH_DEGREES = 1;

// Fog near/far as positions on a scale where 0 is the ground point at the screen
// center and FOG_FAR_PLANE is the far clipping plane (the horizon).
const FOG_NEAR = 1,
    FOG_FAR = 10,
    FOG_FAR_PLANE = 20;

// Fog color as a function of the scene brightness, mirroring the high-zoom
// (>= zoom 7) color ramp of the map style's fog so that the layer fog changes
// color with the time of day together with the map. Each stop is the style's
// hsla() value converted to [r, g, b, a] (r, g, b in 0-255 sRGB, a in 0-1). The
// alpha lets the original texture show through when the fog is fully applied.
const FOG_COLOR_STOPS = [
    {brightness: 0.01, color: [6, 14, 25, 0.9]}, // hsla(213, 63%, 6%, 0.9) - night
    {brightness: 0.2, color: [237, 204, 171, 0.7]}, // hsla(30, 65%, 80%, 0.7) - twilight
    {brightness: 0.4, color: [247, 251, 253, 0.9]} // hsla(200, 60%, 98%, 0.9) - day
];

/**
 * Returns the sunrise and sunset times for the local solar day that contains
 * the given time. SunCalc 2.x anchors getTimes to the UTC calendar day, so the
 * returned times would otherwise jump by a day at 00:00 UTC. Shifting the input
 * by the longitude offset (15 degrees per hour) selects the local solar day, as
 * SunCalc 1.x did internally, moving the unavoidable day boundary to local solar
 * midnight.
 * @param {LngLat} center - The location to compute the times for
 * @param {number} time - The number of milliseconds elapsed since January 1,
 *     1970 00:00:00 UTC
 * @returns {Object} Object with sunrise and sunset Date objects
 */
function getSunTimes(center, time) {
    // ⚠️ **SunCalc 에 아무 시각이나 넘기면 안 된다.** SunCalc 은 주어진 순간에 가장
    // 가까운 태양 남중을 골라 '그 하루' 를 정한다. 그래서 현지 오후 늦게 물으면
    // **다음 날** 일출·일몰이 돌아오고, 호출부의 모든 분기(낮·해질녘·일몰)가
    // 미래 시각과 비교돼 전부 실패한 뒤 마지막 else = 밤 으로 떨어진다.
    //
    // 2026-09-07 실측: suncalc **2.0.2** 에서 16:30 KST 조회가 9/8 일몰을 돌려줘
    // metro·bus 가 대낮에 야간 조명이 됐다. 2.0.1 은 같은 조회가 9/7 을 돌려준다 -
    // `"suncalc": "^2.0.1"` 에 lockfile 이 없어 러너가 2.0.2 를 집으면서 갈렸다.
    //
    // 그래서 조회 시각을 **현지 태양시 정오**로 고정한다. 하루를 우리가 정하므로
    // SunCalc 의 날짜 선택 휴리스틱에 의존하지 않고, 두 버전에서 결과가 같다.
    const solarOffset = center.lng / 15 * HOUR,
        localNoon = Math.floor((time + solarOffset) / DAY) * DAY + DAY / 2 - solarOffset;

    return SunCalc.getTimes(localNoon, center.lat, center.lng);
}

/**
 * Returns the smallest bounding box that contains all the given points
 * @param {Array<LngLatLike>} coords - Array of LngLatLike objects
 * @returns {LngLatBounds} The bounding box
 */
export function getBounds(coords) {
    const bounds = new LngLatBounds();

    for (const coord of coords) {
        bounds.extend(coord);
    }
    return bounds;
}

/**
 * Sets the properties in the specified layer that inherits deck.gl's MabboxLayer.
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @param {string} id - The ID of the layer
 * @param {Object} props - One or more properties to update
 */
export function setLayerProps(map, id, props) {
    getDeckLayer(map, id).setProps(props);
}

/**
 * Returns the light of the given type as currently rendered on the map. It reads
 * the evaluated (possibly transitioning) light values, not the target ones set by
 * setLights, so it reflects what is actually on screen mid-transition.
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @param {string} type - The light type ('ambient' or 'directional')
 * @returns {Object} Object with the following properties:
 *     - color: The light color ([r, g, b], each 0-255)
 *     - intensity: The relative luminance of the color (0-1, 0 is black and
 *       1 is white)
 *     - direction: Only for a directional light. The direction to the light
 *       source as [azimuthal, polar] in degrees, where the azimuthal angle is
 *       0-360 (north-based clockwise; 0 = N, 90 = E, 180 = S, 270 = W) and the
 *       polar angle is 0-90 (0 = straight above, 90 = at the horizon)
 */
function getLight(map, type) {
    // MapLibre 에는 v3 의 style.ambientLight / directionalLight 가 없다.
    // maplibre-compat 이 들고 있는 값을 읽는다 (전이 보간 없음 - 목표값이 즉시 보인다).
    const {properties} = getLights(map).filter(light => light.type === type)[0],
        [r, g, b] = parseCSSColor(properties.color),
        result = {
            color: [r, g, b],
            intensity: .2126 * r / 255 + .7152 * g / 255 + .0722 * b / 255
        };

    if (type === 'directional') {
        // The evaluated direction is a Cartesian unit vector, so convert it back to
        // [azimuthal, polar] in degrees (the inverse of Mapbox's spherical-to-Cartesian
        // conversion) to reflect the value actually rendered, including mid-transition.
        // Mapbox 는 평가된 Cartesian 단위벡터를 주므로 역변환이 필요했지만,
        // 시밍은 우리가 넣은 [azimuthal, polar] 를 그대로 돌려준다.
        const direction = properties.direction;

        result.direction = Array.isArray(direction) && direction[0] === 'literal' ?
            direction[1] : direction;
    }

    return result;
}

/**
 * Returns the directional light as currently rendered on the map (evaluated,
 * possibly mid-transition).
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @returns {Object} Object with the color ([r, g, b], each 0-255), the
 *     intensity (relative luminance of the color, 0-1) and the direction
 *     ([azimuthal, polar] in degrees) of the directional light. See getLight
 *     for the details of each property.
 */
export function getDirectionalLight(map) {
    return getLight(map, 'directional');
}

/**
 * Returns the ambient light as currently rendered on the map (evaluated,
 * possibly mid-transition).
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @returns {Object} Object with the color ([r, g, b], each 0-255) and the
 *     intensity (relative luminance of the color, 0-1) of the ambient light.
 *     See getLight for the details of each property.
 */
export function getAmbientLight(map) {
    return getLight(map, 'ambient');
}

/**
 * Returns the perceived brightness of the lights as currently rendered on the
 * map (evaluated, possibly mid-transition). This is the same value the map style
 * exposes as ['measure-light', 'brightness'], derived from the ambient and
 * directional lights.
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @returns {number} The brightness (0-1), or 0 if it cannot be measured
 */
export function getBrightness(map) {
    return computeBrightness(map);
}

/**
 * Returns true if the map center has moved far enough from the last recorded
 * center that the sun-position-dependent lighting should be refreshed. A missing
 * last center returns true.
 * @param {LngLat} center - The current map center
 * @param {LngLat} last - The map center at the last lighting refresh
 * @returns {boolean} True if the center has moved beyond the threshold
 */
export function hasCenterMoved(center, last) {
    return !last ||
        Math.abs(center.lat - last.lat) >= SUNLIGHT_REFRESH_DEGREES ||
        Math.abs(center.lng - last.lng) >= SUNLIGHT_REFRESH_DEGREES;
}

/**
 * Returns the fog color for the map's current brightness, interpolated across the
 * same color ramp the map style's fog uses.
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @returns {Array<number>} The fog color as [r, g, b, a] (r, g, b in 0-255 sRGB,
 *     a in 0-1)
 */
export function getFogColor(map) {
    const brightness = getBrightness(map),
        stops = FOG_COLOR_STOPS;

    for (let i = 1; i < stops.length; i++) {
        const to = stops[i];

        if (brightness <= to.brightness || i === stops.length - 1) {
            const from = stops[i - 1],
                t = clamp((brightness - from.brightness) / (to.brightness - from.brightness), 0, 1);

            return [
                lerp(from.color[0], to.color[0], t),
                lerp(from.color[1], to.color[1], t),
                lerp(from.color[2], to.color[2], t),
                lerp(from.color[3], to.color[3], t)
            ];
        }
    }
}

/**
 * Returns the fog near/far distances for a camera, mapping the shared FOG_NEAR /
 * FOG_FAR positions from the [0 = screen center, FOG_FAR_PLANE = far clipping
 * plane] scale to actual distances.
 * @param {number} center - The distance to the ground at the screen center
 * @param {number} farPlane - The distance to the far clipping plane (the horizon)
 * @returns {Array<number>} The fog distances as [near, far], in the same units as
 *     the arguments
 */
export function getFogNearFar(center, farPlane) {
    const unit = (farPlane - center) / FOG_FAR_PLANE;

    return [center + FOG_NEAR * unit, center + FOG_FAR * unit];
}

/**
 * Sets the sunlight at a specific time to the map
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @param {number} time - The number of milliseconds elapsed since January 1,
 *     1970 00:00:00 UTC
 * @param {number} shadowIntensity - Determines the shadow strength
 * @param {boolean} shadowOnly - If true, only the shadow intensity is updated
 *     while the brightness-affecting properties are left unchanged
 * @param {boolean} _transition - 무시된다. Mapbox 는 lights 를 style transition 으로
 *     보간했지만 MapLibre 에는 lights API 자체가 없어 maplibre-compat 이 값을 들고
 *     있고, 그건 스냅이다. 호출부 시그니처를 지키려고 자리만 남긴다.
 */
export function setSunlight(map, time, shadowIntensity, shadowOnly, _transition) {
    const center = map.getCenter(),
        {sunrise, sunset} = getSunTimes(center, time),
        // At high latitudes there may be no sunrise/sunset (polar day/night), in
        // which case SunCalc returns null; fall back to NaN so the branches below
        // are skipped and the polar day/night case is handled explicitly.
        sunriseTime = sunrise ? sunrise.getTime() : NaN,
        sunsetTime = sunset ? sunset.getTime() : NaN,
        {azimuth, altitude} = SunCalc.getPosition(time, center.lat, center.lng),
        sunAzimuth = azimuth,
        sunAltitude = 90 - altitude;
    let t, ambient, directional, sun;

    if ((isNaN(sunriseTime) || isNaN(sunsetTime)) && altitude > 0) {
        // Polar day: the sun stays above the horizon all day (no sunrise/sunset),
        // so use daytime lighting. Polar night (sun below the horizon) falls through
        // to the Night branch below.
        ambient = {
            r: 255,
            g: 255,
            b: 255,
            i: .7
        };
        directional = {
            r: 255,
            g: 255,
            b: 255,
            i: .3,
            w: 1
        };
        sun = {
            azimuth: sunAzimuth,
            altitude: sunAltitude
        };
    } else if (time >= sunriseTime - HOUR / 2 && time < sunriseTime) {
        // Night to sunrise
        const sunrisePosition = SunCalc.getPosition(sunriseTime, center.lat, center.lng);

        t = (time - sunriseTime) / (HOUR / 2) + 1;
        ambient = {
            r: lerp(0, 153, t),
            g: lerp(22, 179, t),
            b: lerp(56, 204, t),
            i: lerp(.5, .65, t)
        };
        directional = {
            r: 74,
            g: 74,
            b: 74,
            i: lerp(.5, 1, t),
            w: 0
        };
        sun = {
            azimuth: lerp(210, sunrisePosition.azimuth, t),
            altitude: 20
        };
    } else if (time >= sunriseTime && time < sunriseTime + HOUR) {
        // Sunrise to day
        t = (time - sunriseTime) / HOUR;
        ambient = {
            r: lerp(153, 255, t),
            g: lerp(179, 255, t),
            b: lerp(204, 255, t),
            i: lerp(.65, .7, t)
        };
        directional = {
            r: lerp(254, 255, t),
            g: lerp(202, 255, t),
            b: lerp(139, 255, t),
            i: lerp(.6, .3, t),
            w: 1
        };
        sun = {
            azimuth: sunAzimuth,
            altitude: sunAltitude
        };
    } else if (time >= sunriseTime + HOUR && time < sunsetTime - HOUR) {
        // Day
        ambient = {
            r: 255,
            g: 255,
            b: 255,
            i: .7
        };
        directional = {
            r: 255,
            g: 255,
            b: 255,
            i: .3,
            w: 1
        };
        sun = {
            azimuth: sunAzimuth,
            altitude: sunAltitude
        };
    } else if (time >= sunsetTime - HOUR && time < sunsetTime) {
        // Day to sunset
        t = (time - sunsetTime) / HOUR + 1;
        ambient = {
            r: lerp(255, 235, t),
            g: lerp(255, 197, t),
            b: lerp(255, 173, t),
            i: lerp(.7, .65, t)
        };
        directional = {
            r: lerp(255, 254, t),
            g: lerp(255, 194, t),
            b: lerp(255, 134, t),
            i: lerp(.3, .6, t),
            w: 1
        };
        sun = {
            azimuth: sunAzimuth,
            altitude: sunAltitude
        };
    } else if (time >= sunsetTime && time < sunsetTime + HOUR / 2) {
        // Sunset to night
        const sunsetPosition = SunCalc.getPosition(sunsetTime, center.lat, center.lng);

        t = (time - sunsetTime) / (HOUR / 2);
        ambient = {
            r: lerp(235, 0, t),
            g: lerp(197, 22, t),
            b: lerp(173, 56, t),
            i: lerp(.65, .5, t)
        };
        directional = {
            r: 74,
            g: 74,
            b: 74,
            i: lerp(1, .5, t),
            w: 0
        };
        sun = {
            azimuth: lerp(sunsetPosition.azimuth, 210, t),
            altitude: 20
        };
    } else {
        // Night
        ambient = {
            r: 0,
            g: 22,
            b: 56,
            i: .5
        };
        directional = {
            r: 74,
            g: 74,
            b: 74,
            i: .5,
            w: 0
        };
        sun = {
            azimuth: 210,
            altitude: 20
        };
    }

    const shadowIntensityValue = directional.w * shadowIntensity;

    if (shadowOnly) {
        // Mode toggle (underground/ground): the brightness-affecting values (color, intensity
        // and direction) are unchanged, so update shadow-intensity alone. Changing the brightness
        // here would force a re-evaluation/re-upload of all model layer instances and freeze the
        // main thread.
        const lights = getLights(map);

        for (const light of lights) {
            if (light.type === 'directional') {
                light.properties = Object.assign({}, light.properties, {
                    'shadow-intensity': shadowIntensityValue
                });
            }
        }
        setMapLights(map, lights);
    } else {
        // setLights captures the global style transition synchronously, so drive the
        // snap/animate through it - 0 to snap continuous changes, the default duration
        // to animate a jump - and restore it right after so nothing else is affected.
        // 시밍은 스냅이라 style transition 을 건드릴 필요가 없다. MapLibre 에도
        // style.transition 이 있지만 lights 를 보간하지 않으므로 그대로 둔다.
        setMapLights(map, [{
            id: 'ambient',
            type: 'ambient',
            properties: {
                color: `rgb(${ambient.r}, ${ambient.g}, ${ambient.b})`,
                intensity: ambient.i
            }
        }, {
            id: 'directional',
            type: 'directional',
            properties: {
                direction: ['literal', [sun.azimuth, sun.altitude]],
                color: `rgb(${directional.r}, ${directional.g}, ${directional.b})`,
                intensity: directional.i,
                'cast-shadows': true,
                'shadow-intensity': shadowIntensityValue
            }
        }]);
    }
    // MapLibre 에서 sky 는 레이어가 아니라 루트 속성이다 (map.setSky). Mapbox 의
    // 'sky-atmosphere-sun' 처럼 태양 방위를 직접 받지 않으므로, 고도만으로 하늘색과
    // 지평선 혼합을 움직여 근사한다. 방위(azimuth)는 반영되지 않는다 - 해가 어느
    // 쪽에 있는지가 하늘 그라데이션에 안 나타난다.
    if (map.setSky) {
        // 고도 0(지평선)에서 1(천정)로 정규화. 낮에 파랗고 해질녘에 붉어진다.
        const t = clamp((90 - sunAltitude) / 90, 0, 1);

        map.setSky({
            'sky-color': `hsl(220, 100%, ${lerp(70, 35, t)}%)`,
            'horizon-color': `hsl(${lerp(220, 25, t)}, ${lerp(60, 90, t)}%, ${lerp(85, 55, t)}%)`,
            'fog-color': `hsl(220, 30%, ${lerp(92, 60, t)}%)`,
            'horizon-fog-blend': 0.6,
            'sky-horizon-blend': 0.6,
            'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 0, 5, 0.3, 8, 1]
        });
    }
}

/**
 * Checks if the background color of the map is dark.
 *
 * MapLibre 판에는 `actual`(전이 중 실제 화면값) 인자가 없다. 평가된 paint 에
 * 접근할 방법이 없어 지킬 수 없는 약속이라 조용히 무시하는 대신 지웠다.
 * @param {maplibregl.Map} map - MapLibre's Map object
 * @returns {boolean} True if the background color of the map is dark
 */
export function hasDarkBackground(map) {
    const light = getLights(map).filter(({type}) => type === 'ambient')[0],
        lightColorElements = parseCSSColor(light.properties.color),
        lightIntensity = light.properties.intensity,
        lr = lightColorElements[0] / 255 * lightIntensity,
        lg = lightColorElements[1] / 255 * lightIntensity,
        lb = lightColorElements[2] / 255 * lightIntensity;

    // Mapbox 는 평가된 paint(전이 중 실제 화면값)를 줬지만 MapLibre 에는 그
    // 접근이 없다. 선언값 경로 하나로 합친다 - 전이 중 한두 프레임의 판정이
    // 달라질 수 있고, 이 함수는 라벨 색 선택에만 쓰인다.
    return BG_LAYER_IDS.reduce((value, id) => {
        const [r, g, b] = parseCSSColor(map.getPaintProperty(id, 'background-color')),
            a = valueOrDefault(map.getPaintProperty(id, 'background-opacity'), 1);
        return value + luminance({r: r * lr * a, g: g * lg * a, b: b * lb * a});
    }, 0) < 127.5;
}

/**
 * Returns an array of the style opacity information retrieved from map layers.
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @param {string} metadataKey - Metadata key to filter
 * @returns {Array<Object>} Array of the style opacity objects
 */
export function getStyleOpacities(map, metadataKey) {
    const {_layers, _order} = map.style,
        propMapping = {
            'background-underground': 1,
            'building-underground-underground': ['interpolate', ['linear'], ['zoom'], 14.5, 0, 15, 1]
        },
        opacities = [];

    _order.map(id => _layers[id]).filter(({metadata}) =>
        metadata && metadata[metadataKey]
    ).forEach(({id, type, metadata}) => {
        if (type === 'custom') {
            opacities.push({id, metadata});
            return;
        }

        const key = `${type.replace('symbol', 'icon')}-opacity`,
            prop = propMapping[id] || valueOrDefault(map.getPaintProperty(id, key), 1);

        if (!isNaN(prop)) {
            opacities.push({id, key, opacity: prop, metadata});
        } else if (includes(['match', 'step', 'interpolate'], prop[0])) {
            const opacity = [];

            prop.forEach((value, index) => {
                if ((index % 2 === (prop[0] === 'match' ? 1 : 0) || index === prop.length - 1) && !isNaN(value)) {
                    opacity.push({index, value});
                }
            });
            opacities.push({id, key, opacity, metadata});
        }
    });
    return opacities;
}

/**
 * Sets style opacities based on the style opacity objects and factor
 * @param {mapboxgl.Map} map - Mapbox's Map object
 * @param {Array<Object>} styleOpacities - Array of the style opacity objects
 * @param {string | Array<string>} factorKey - Metadata key for the factor to multiply
 */
export function setStyleOpacities(map, styleOpacities, factorKey) {
    const duration = getTransitionDuration(map);

    for (const {id, key, opacity, metadata} of styleOpacities) {
        let factor, prop;

        if (Array.isArray(factorKey)) {
            factor = factorKey.reduce((value, key) => valueOrDefault(value, metadata[key]), undefined);
        } else {
            factor = metadata[factorKey];
        }

        if (key) {
            if (Array.isArray(opacity)) {
                prop = map.getPaintProperty(id, key);
                for (const {index, value} of opacity) {
                    prop[index] = value * factor;
                }
            } else {
                prop = opacity * factor;
            }
            map.setPaintProperty(id, key, prop);
        } else {
            const start = performance.now(),
                current = valueOrDefault(getDeckLayer(map, id).props.opacity, 1);

            // Workaround for deck.gl's transitions property which doesn't work as extected
            (function repeat() {
                const elapsed = performance.now() - start;

                setLayerProps(map, id, {
                    opacity: lerp(current, factor, Math.min(elapsed / duration, 1))
                });
                if (elapsed < duration) {
                    requestAnimationFrame(repeat);
                }
            })();
        }
    }
}
